import classNames from "classnames";
import React, { ReactNode } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import GridLayout, { WidthProvider } from "react-grid-layout";

import { MetricsDashboardData, MetricsDashboardGraph, MetricsDashboardGraphType } from "../../__generated__/api";
import { useDebounce } from "../../debouncer";
import COMPONENT_TO_TYPE from "./graphs";
import Styles from "./Reports.module.scss";
import { useIsFirstLoad } from "../../use-effect";
import { Time } from "../../date";

const ReactGridLayout = WidthProvider(GridLayout);

export interface RowGroup {
    rows: MetricsDashboardData;
    columnIndexMap: Record<string, number>;
    labelMap: Record<string, string>;
}

const MIN_HEIGHTS_BY_TYPE = {
    [MetricsDashboardGraphType.Table]: 2,
    [MetricsDashboardGraphType.Count]: 1,
    [MetricsDashboardGraphType.PieChart]: 2,
    [MetricsDashboardGraphType.TimeSeries]: 2,
    [MetricsDashboardGraphType.BarGraph]: 2,
    [MetricsDashboardGraphType.LineGraph]: 2,
};

const HEIGHT_SEGMENT = 120;
export const MAX_COLUMNS = 12;
export default function ReportDisplay({
    graphs,
    isAdmin,
    createMenu,
    rowGroups,
    onChange,
    className,
}: {
    graphs: MetricsDashboardGraph[];
    isAdmin: boolean;
    createMenu: (value: MetricsDashboardGraph) => ReactNode;
    rowGroups: Record<string, RowGroup>;
    onChange: (value: MetricsDashboardGraph[]) => void;
    className?: string;
}) {
    const debounceOnChange = useDebounce<MetricsDashboardGraph[]>(onChange, 500);
    const isFirstLoad = useIsFirstLoad({
        delay: Time.SECOND,
    });

    return (

        <ReactGridLayout
            cols={12}
            margin={[24, 24]}
            rowHeight={HEIGHT_SEGMENT}
            containerPadding={[16, 0]}
            layout={graphs.map(graph => ({
                /* eslint-disable id-length */
                i: graph.id,
                x: graph.locationX,
                y: graph.locationY,
                w: graph.width,
                h: graph.height,
                minW: 2,
                minH: MIN_HEIGHTS_BY_TYPE[graph.type] ?? 2,
                resizeHandles: isAdmin ? ["se"] : undefined,
                isDraggable: isAdmin,
                isResizable: isAdmin,
            /* eslint-enable id-length */
            }))}
            onLayoutChange={layouts => {
                if (isFirstLoad) {
                    return;
                }
                const copy = [...graphs];

                let hasChanged = false;
                layouts.forEach(layout => {
                    const index = copy.findIndex(row => row && row.id === layout.i);
                    if (index < 0) {
                        return;
                    }

                    const current = copy[index];
                    const isChanged = current.locationX !== layout.x
                        || current.locationY !== layout.y
                        || current.width !== layout.w
                        || current.height !== layout.h;

                    if (isChanged) {
                        hasChanged = true;
                        copy[index] = {
                            ...copy[index],
                            locationX: layout.x,
                            locationY: layout.y,
                            width: layout.w,
                            height: layout.h,
                        };
                    }
                });
                if (hasChanged) {
                    debounceOnChange(copy);
                }
            }}
            onResize={(layout, oldItem, newItem) => {
                const index = graphs.findIndex(row => row && row.id === oldItem.i);
                if (index < 0) {
                    return;
                }
                const copy = [...graphs];
                copy[index] = {
                    ...copy[index],
                    locationX: newItem.x,
                    locationY: newItem.y,
                    width: newItem.w,
                    height: newItem.h,
                };
                debounceOnChange(copy);
            }}
            className={classNames(Styles.grid, isAdmin ? Styles["grid-admin"] : "", className)}
            draggableHandle={`.${Styles["report-widget-draggable"]}`}
        >

            {graphs.map(graph => {
                const Component = COMPONENT_TO_TYPE[graph.type];

                const dataSource = rowGroups[graph.dataSource];
                return dataSource ? (
                    <div
                        key={graph.id}
                        id={`graph-${graph.id.replace(/[^0-9a-zA-Z]/g, "")}`}
                        className={classNames(
                            Styles["report-widget"],
                            isAdmin ? Styles["report-widget-admin"] : "",
                        )}
                    >
                        {Component
                            ? (
                                <Component
                                    graph={graph}
                                    data={dataSource.rows as MetricsDashboardData}
                                    columnIndexMap={dataSource.columnIndexMap}
                                    labelMap={dataSource.labelMap}
                                    createMenu={createMenu}
                                    isAdmin={isAdmin}
                                />
                            )
                            : null}
                    </div>

                ) : undefined;
            })}

        </ReactGridLayout>
    );
}

ReportDisplay.defaultProps = {
    className: undefined,
};
